import { useMemo } from 'react';
import {
  IncludesSubscriptionBackfillDetail,
  SubscriptionBackfillBatchName,
  SubscriptionBackfillState,
  SubscriptionType,
} from '@weave/schema-gen-ts/dist/shared/call-intelligence/plg/subscription_backfill_enums.pb';
import { Permission } from '@weave/schema-gen-ts/dist/shared/waccess/acls.pb';
import { CallIntelligenceWetags } from '@wetags/call-intelligence';
import dayjs from 'dayjs';
import { useGetSubscriptionBackfillStatusQuery } from '@frontend/api-call-intel';
import { hasSchemaACL } from '@frontend/auth-helpers';
import { useCallIntelSubscription } from '@frontend/call-intel-core';
import { useAppScopeStore } from '@frontend/scope';
import { BACKFILL_RELEVANCE_WINDOW_DAYS, BACKFILL_WETAG } from '../constants';
import { LocationBackfillStatus } from '../types';
import { isBackfillIncomplete, isBatch1Complete } from '../utils';

const BACKFILL_STATUS_INCLUDES = [
  IncludesSubscriptionBackfillDetail.INCLUDES_SUBSCRIPTION_BACKFILL_LOCATION_STATUSES,
  IncludesSubscriptionBackfillDetail.INCLUDES_SUBSCRIPTION_BACKFILL_BATCH_STATUS,
];

const CACHE_DURATION = 15 * 60 * 1000; // 15 minutes

export const useBackfillStatus = (locationIds?: string[]) => {
  const [isWetagEnabled] = CallIntelligenceWetags.useWetag(BACKFILL_WETAG);
  const { selectedLocationIds } = useAppScopeStore();

  const effectiveLocationIds = locationIds !== undefined ? locationIds : selectedLocationIds;

  const accessibleLocationIds = useMemo(() => {
    return (effectiveLocationIds || [])
      .filter((locId) => hasSchemaACL(locId, Permission.CALL_INTELLIGENCE_READ))
      .sort((a, b) => a.localeCompare(b));
  }, [effectiveLocationIds]);

  const {
    trialActiveLocationIds,
    callIntelBasePlanLocationIds,
    callIntelProPlanLocationIds,
    isLoading: isSubscriptionLoading,
  } = useCallIntelSubscription({
    locationIds: accessibleLocationIds,
  });

  const hasAnyEligibleLocation =
    trialActiveLocationIds.length > 0 ||
    callIntelBasePlanLocationIds.length > 0 ||
    callIntelProPlanLocationIds.length > 0;

  const { data: backfillStatus, isLoading: isBackfillLoading } = useGetSubscriptionBackfillStatusQuery({
    request: {
      includes: BACKFILL_STATUS_INCLUDES,
      locationIds: accessibleLocationIds,
    },
    options: {
      enabled: isWetagEnabled && hasAnyEligibleLocation && accessibleLocationIds.length > 0,
      retry: false,
      cacheTime: CACHE_DURATION,
      staleTime: CACHE_DURATION,
    },
  });

  const locationStatuses: LocationBackfillStatus[] = useMemo(() => {
    if (!backfillStatus?.locationStatuses) return [];

    return backfillStatus.locationStatuses
      .map(
        ({
          locationId = '',
          activationAt,
          batches,
          subscriptionType = SubscriptionType.SUBSCRIPTION_TYPE_UNSPECIFIED,
          state,
          lastUpdatedAt,
        }) => {
          const backfillDays = batches?.find(
            (batch) => batch.batchName === SubscriptionBackfillBatchName.SUBSCRIPTION_BACKFILL_BATCH_NAME_BATCH_2
          )?.target;

          const lastProgressAt = lastUpdatedAt ?? activationAt;

          const isBatch1Ready = isBatch1Complete(state);

          const isBatch2Ready = state === SubscriptionBackfillState.SUBSCRIPTION_BACKFILL_STATE_FULLY_COMPLETE;

          const isBackfillIncompleteStatus = isBackfillIncomplete(state);

          const batch1CallCount = batches?.find(
            (batch) => batch.batchName === SubscriptionBackfillBatchName.SUBSCRIPTION_BACKFILL_BATCH_NAME_BATCH_1
          )?.target;

          return {
            locationId,
            subscriptionType,
            backfillDays,
            lastProgressAt,
            isBatch1Ready,
            isBatch2Ready,
            isBackfillIncomplete: isBackfillIncompleteStatus,
            state: state ?? SubscriptionBackfillState.SUBSCRIPTION_BACKFILL_STATE_UNSPECIFIED,
            batch1CallCount,
          };
        }
      )
      .filter((loc) => !!loc.locationId);
  }, [backfillStatus]);

  const relevantLocationStatuses = useMemo(() => {
    const relevanceCutoff = dayjs().subtract(BACKFILL_RELEVANCE_WINDOW_DAYS, 'day');

    return locationStatuses.filter((loc) => {
      if (!loc.lastProgressAt) return false;
      const lastProgress = dayjs(loc.lastProgressAt);
      if (!lastProgress.isValid()) return false;
      return !lastProgress.isBefore(relevanceCutoff);
    });
  }, [locationStatuses]);

  const hasBackfillAccess = useMemo(() => {
    return relevantLocationStatuses.some((loc) => hasSchemaACL(loc.locationId, Permission.CALL_INTELLIGENCE_READ));
  }, [relevantLocationStatuses]);

  const isMixedSubscriptionPlan = useMemo(() => {
    if (relevantLocationStatuses.length <= 1) return false;
    const locationsWithBackfillStatus = relevantLocationStatuses.filter(
      (loc) => loc.subscriptionType && loc.subscriptionType !== SubscriptionType.SUBSCRIPTION_TYPE_UNSPECIFIED
    );
    if (locationsWithBackfillStatus.length <= 1) return false;
    const initialType = locationsWithBackfillStatus[0].subscriptionType;
    return locationsWithBackfillStatus.some((loc) => loc.subscriptionType !== initialType);
  }, [relevantLocationStatuses]);

  const aggregatedBannerState = useMemo(() => {
    const locationsWithBackfillStatus = relevantLocationStatuses.filter(
      (loc) => loc.subscriptionType && loc.subscriptionType !== SubscriptionType.SUBSCRIPTION_TYPE_UNSPECIFIED
    );

    if (!locationsWithBackfillStatus.length) {
      return {
        isMixedSubscriptionPlan: false,
        aggregatedSubscriptionType: SubscriptionType.SUBSCRIPTION_TYPE_UNSPECIFIED,
        batch1Complete: false,
        batch2Complete: false,
        batch2PartialComplete: false,
        batch2CompleteCount: 0,
        aggregatedBackfillDays: undefined as number | undefined,
      };
    }

    const aggregatedSubscriptionType = !isMixedSubscriptionPlan
      ? locationsWithBackfillStatus[0].subscriptionType
      : SubscriptionType.SUBSCRIPTION_TYPE_UNSPECIFIED;

    // For mixed batch-1: only consider non-BASE locations
    const nonBaseLocations = locationsWithBackfillStatus.filter(
      (loc) =>
        loc.subscriptionType !== SubscriptionType.SUBSCRIPTION_TYPE_UNSPECIFIED &&
        loc.subscriptionType !== SubscriptionType.SUBSCRIPTION_TYPE_BASE
    );

    let batch1Complete = false;
    let batch2Complete = false;
    let batch2PartialComplete = false;
    let batch2CompleteCount = 0;

    if (isMixedSubscriptionPlan) {
      batch1Complete = nonBaseLocations.length > 0 && nonBaseLocations.every((loc) => loc.isBatch1Ready);
      batch2Complete = nonBaseLocations.length > 0 && nonBaseLocations.every((loc) => loc.isBatch2Ready);
      batch2CompleteCount = nonBaseLocations.filter((loc) => loc.isBatch2Ready).length;
      batch2PartialComplete = false;
    } else {
      batch1Complete =
        locationsWithBackfillStatus.length > 0 && locationsWithBackfillStatus.every((loc) => loc.isBatch1Ready);
      batch2Complete =
        locationsWithBackfillStatus.length > 0 && locationsWithBackfillStatus.every((loc) => loc.isBatch2Ready);
      batch2CompleteCount = locationsWithBackfillStatus.filter((loc) => loc.isBatch2Ready).length;
      batch2PartialComplete = false;
    }

    const firstBatch2Location = locationsWithBackfillStatus.find((loc) => loc.isBatch2Ready);
    const aggregatedBackfillDays = firstBatch2Location?.backfillDays;

    return {
      isMixedSubscriptionPlan,
      aggregatedSubscriptionType,
      batch1Complete,
      batch2Complete,
      batch2PartialComplete,
      batch2CompleteCount,
      aggregatedBackfillDays,
    };
  }, [relevantLocationStatuses, isMixedSubscriptionPlan]);

  return {
    locationStatuses,
    relevantLocationStatuses,
    isLoading: isSubscriptionLoading || isBackfillLoading,
    isWetagEnabled,
    hasBackfillAccess,
    isMixedSubscriptionPlan,
    aggregatedBannerState,
    isFirstBatchBackfillCompleted: isBatch1Complete(backfillStatus?.aggregateState),
  };
};
